import {
 CSVProfile,
 IChartData,
 IComponentProperties,
 IDateRange,
 IVisOptions,
 Pagination,
} from '../../types'

import { ColorUtils } from '../utils/color-utils'
import { LabelUtils } from '../utils/label-utils'
import { NamingUtils } from '../utils/naming-utils'
import { ResizeInterval } from '../../charts-factory'
import _ from 'lodash'
import { ReduxService } from '../service/redux.service'
import * as CONSTANTS from '../commons/constants'

export class BaseVisualization {
 static config = {
  selector: 'baseVisualization',
  controller: BaseVisualization,
  bindings: {
   visualizationId: '<',
   triggerUpdate: '<',
   vizData: '<',
   visTitle: '<',
   size: '<',
   isLoading: '<',
   isError: '<',
   dateRange: '<',
   properties: '<',
   csvExportProfiles: '=?',
   locationType: '<',
   pagination: '<',
   options: '<',
   visualizationMetaData: '<?',
   isRestricted: '<',
   treatments: '<',
  },
 }

 // Internal
 private RESIZE_AFTER_MS = ResizeInterval

 // Bindings
 public visualizationData: IChartData = {}
 public visualizationDataArray: Array<IChartData> = []
 public visualizationOptions: any = {}
 public vizData: any
 public visualizationId: string
 public triggerUpdate: any
 public visTitle: string
 public size: string
 public isError: boolean
 public dateRange: IDateRange
 public granularity
 public visOptions: IVisOptions = { colorOptions: '' }
 public properties: IComponentProperties
 public isLoading
 public pagination: Pagination
 public csvExportProfiles: CSVProfile[]
 public options: any
 public namingUtils = NamingUtils
 public colorUtils = new ColorUtils()
 public labelUtils = new LabelUtils()
 public visualizationMetaData
 public isRestricted
 public locationType: any
 public treatments: any;

 public BaseNumbers = {
  E: 1000000000000000000, // quintillion
  Q: 1000000000000000, // quadrillion, standard symbol is 'P'
  T: 1000000000000, // trillion
  B: 1000000000, // billion, standard symbol is 'G'
  M: 1000000, // million
  k: 1000, // thousand
  '': 1,
 }
 public dateRangeTypeEnum = {
  today: 'wc-measure__date-range--yesterday',
  thisWeek: 'wc-measure__date-range--last-week',
  last7Days: 'wc-measure__date-range--previous-seven-days',
  last14Days: 'wc-measure__date-range--previous-fourteen-days',
  thisMonth: 'wc-measure__date-range--this-month',
  last30Days: 'wc-measure__date-range--previous-thirty-days',
  thisQuarter: 'wc-measure__date-range--last-quarter',
  last90Days: 'wc-measure__date-range--previous-ninty-days',
  thisYear: 'wc-measure__date-range--this-year',
  divider: 'wc-measure__date-range--custom',
  custom: 'wc-measure__date-range--custom',
 }
 public CONSTANTS = CONSTANTS

 /*@ngInject*/
 static $inject = ['reduxService']
 constructor(public reduxService: ReduxService) {}

 getApi(API: { redraw: () => void }) {
  let resizeTimeout
  window.addEventListener('resize', () => {
   clearTimeout(resizeTimeout)
   resizeTimeout = setTimeout(() => API.redraw(), this.RESIZE_AFTER_MS)
  })
 }

 updatePayload(type: string, operation: string, filterId: string, value: string) {
  return {
   type: type,
   operation: operation,
   filterId: filterId,
   value: value,
   target: this.visualizationId,
  }
 }

 updatePayloadDate(startDate: number, endDate: number) {
  return {
   type: 'DATE_RANGE_CHANGE',
   startDate: startDate,
   endDate: endDate,
   target: this.visualizationId,
  }
 }

 isObjEmpty(data) {
  if (!data) return true
  return Object.keys(data).length === 0 && data.constructor === Object
 }

 numberTransform(input: number): string {
  let roundedInput: number = Math.round(input)
  let truncatedNumber: number
  let fixedNumber: String
  let numberKey: String = ''
  let dotIndex: Number

  if (input === null || isNaN(roundedInput)) {
   return '-'
  }

  if (roundedInput === 0 || roundedInput === -0) {
   return '0'
  }

  if (Math.abs(roundedInput) >= this.BaseNumbers.E) {
   return `${roundedInput}`
  }

  for (let key of Object.keys(this.BaseNumbers)) {
   if (Math.abs(roundedInput) >= this.BaseNumbers[key]) {
    truncatedNumber = roundedInput / this.BaseNumbers[key]
    numberKey = key
    break
   }
  }

  dotIndex = Math.abs(truncatedNumber)
   .toString()
   .indexOf('.')

  if (dotIndex === 1) {
   fixedNumber = truncatedNumber.toFixed(2)
  } else if (dotIndex === 2) {
   fixedNumber = truncatedNumber.toFixed(1)
  } else {
   fixedNumber = truncatedNumber.toFixed(0)
  }

  // localize the number via angular number pipe and append number key before returning
  return `${fixedNumber} ${numberKey}`
 }

 /**
  * Function to get date range type string.
  * Since date range type is known. Easier to define enum
  * instead, and we can apply updates from there.
  * @returns the date type string e.g. days, month @see dateRangeTypeEnum
  */
 dateRangeName(): string {
  return this.dateRangeTypeEnum[this.dateRange.type]
 }

 /**
  * @deprecated to be reomved on next update
  * @param name
  */
 singleDate(name: string): string {
  switch (name) {
   case 'Today':
    return 'day'
    break

   case 'Custom':
    return 'custom range'
    break

   default:
    break
  }
 }

 hasChanges(change) {
  return !_.isEmpty(change) && typeof change.currentValue !== 'undefined'
 }

 setMetaData(data) {
  this.visualizationMetaData = data
 }

 sortCompareObject(data, metaData) {
  let orderData = {}
  metaData.map(compareItem => {
   orderData[compareItem.name] = data[compareItem.name]
  })
  return orderData
 }

 sortCompareArray(data, metaData) {
  let orderData = []
  metaData.map(compareItem => {
   orderData[compareItem.name] = data[compareItem.name]
  })
  return orderData
 }

 sortCompareLoop(data, key, metaData) {
  let orderData = []

  metaData.map(compareItem => {
   let index = _.findIndex(data, [key, compareItem.name])
   if (index > -1) {
    orderData.push(data[index])
   } else {
    orderData.push({ key: compareItem.name, value: 0 })
   }
  })

  return orderData
 }
 sortCompareLoopDeep(data, key, metaData) {
  data.map(data => {
   data.data = this.sortCompareLoop(data.data, 'key', metaData)
  })
  return data
 }

 isChartClickable() {
  let searchType = this.reduxService.getValue('input.selectedInputType')
  if (searchType === CONSTANTS.INPUT_TYPE_COMBINED) {
   return false
  }
  return true
 }
}
